import { Alert, Button, Form, Row, Space, Typography, Col } from "antd";
import DynamicForm from "../../controls/DynamicForm";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import View from "../../controls/View";
import {
  addInvoice,
  updateInvoice,
  deleteInvoice,
  getCompanies,
  getInvoice,
  getInvoicesItems,
} from "../../repository/data.repository";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import { ModalSize } from "../../controls/InModal.default";
import { nameOf } from "../../utils/typescript";
import { getProblemMessage } from "../../utils/http";
import {
  Invoice,
  InvoiceItem,
  MAX_PAGE_SIZE,
} from "../../repository/data.default";
import { QueryOperation } from "../../code/search-query/search-query";
import { createSearchRequest } from "../../hooks/useSearchRequest";
import { InvoiceForm } from "./InvoiceView.default";
import InvoiceTable from "./InvoiceTable";
import InvoiceDetails from "./InvoiceDetails";
import InvoiceItemsManagment from "./InvoiceItemsManagment";

export interface InvoiceViewProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "view" | "delete";
  readonly uid?: string;
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
  readonly initialValues?: Invoice;
}

function InvoiceView(props: InvoiceViewProps) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [invoiceForm] = Form.useForm();
  const invoiceValues = Form.useWatch<InvoiceForm>([], invoiceForm);
  const [valid, setValid] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [invoiceDetails, setInvoiceDetails] = useState<Invoice>();

  // Lifecycle

  useEffect(() => {
    if (props.open && props.mode !== "delete") {
      invoiceForm.setFieldsValue(props.initialValues);
      loadData();
    } else {
      reset();
    }
  }, [props.open, props.uid]);

  useEffect(() => {
    invoiceForm
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [invoiceForm, invoiceValues, props.open]);

  // Events

  function onSubmit(): void {
    const { company, description, isPaid, dueDate, totalAmount } =
      invoiceValues;
    setLoading(true);
    setErrorMessage("");
    if (props.mode === "add") {
      addInvoice({
        companyUid: company?.value,
        description,
        isPaid,
        dueDate: dayjs(dueDate).format("YYYY-MM-DD"),
        totalAmount,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      updateInvoice({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        companyUid: company?.value,
        description,
        isPaid,
        dueDate: dayjs(dueDate).format("YYYY-MM-DD"),
        totalAmount,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "delete") {
      deleteInvoice(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onClose(): void {
    props.onClose?.();
  }

  // Methods

  function loadData(): void {
    setErrorMessage("");
    if (props?.uid) {
      setLoading(true);
      const searchQuery = {
        conditions: [
          {
            field: nameOf<InvoiceItem>("invoiceUid"),
            operation: QueryOperation.Equal,
            values: [props.initialValues?.uid!],
          },
        ],
      };
      Promise.all([
        getInvoice(props?.uid!),
        getInvoicesItems({
          searchQuery,
          pageNumber: 0,
          pageSize: MAX_PAGE_SIZE,
        }),
      ])
        .then(([invoice, invoiceItems]) => {
          setInvoiceItems(invoiceItems.data);
          setInvoiceDetails(invoice);
          setLoading(false);
        })
        .catch((err) => {
          setErrorMessage(getProblemMessage(err));
          setLoading(false);
        });
    }
  }

  function loadInvoiceItems(): void {
    if (props.uid) {
      const searchQuery = {
        conditions: [
          {
            field: nameOf<InvoiceItem>("invoiceUid"),
            operation: QueryOperation.Equal,
            values: [props.initialValues?.uid!],
          },
        ],
      };
      getInvoicesItems({
        searchQuery,
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      })
        .then((invoiceItems) => {
          setInvoiceItems(invoiceItems.data);
          setLoading(false);
        })
        .catch((err) => {
          setErrorMessage(getProblemMessage(err));
          setLoading(false);
        });
    }
  }

  function createMode(): string {
    switch (props.mode) {
      case "add":
        return "Add";
      case "update":
        return "Save";
      case "delete":
        return "Delete";
    }

    return "";
  }

  function reset(): void {
    setErrorMessage("");
    setLoading(false);
    invoiceForm.resetFields();
  }

  const companiesRequest = createSearchRequest(getCompanies, "name");

  function swichModalSize(): ModalSize {
    switch (props.mode) {
      case "add":
        return "medium";
      case "update":
        return "xxl";
      case "delete":
        return "small";
    }

    return "medium";
  }

  return (
    <>
      {props.mode === "view" && (
        <View
          open={props.open}
          loading={loading}
          title={"Invoice"}
          onBack={onClose}
        >
          {errorMessage && (
            <Alert
              className="mb-2 self-start"
              message={errorMessage}
              type="error"
            />
          )}
          {props.mode === "view" && (
            <Row className="flex w-2/4 flex-col items-start gap-6">
              <Col>
                <InvoiceDetails invoice={invoiceDetails} />
              </Col>
              <Col>
                <InvoiceTable invoiceItems={invoiceItems} />
              </Col>
            </Row>
          )}
        </View>
      )}

      {props.mode !== "view" && (
        <InModal
          open={props.open}
          size={swichModalSize()}
          title={modalTitle(props.mode, "Invoice", undefined)}
          onCancel={onClose}
          actions={
            <Space>
              <Button disabled={loading} onClick={onClose}>
                Cancel
              </Button>
              <Button
                type={"primary"}
                danger={props.mode === "delete"}
                loading={loading}
                onClick={onSubmit}
                disabled={!valid && props.mode !== "delete"}
              >
                {createMode()}
              </Button>
            </Space>
          }
        >
          {props.mode !== "delete" && (
            <Row className="flex gap-4">
              <Col
                className={`${props.mode === "update" ? "w-1/5" : "flex-1"} p-1`}
              >
                <DynamicForm
                  className="w-auto"
                  form={invoiceForm}
                  schema={{
                    groups: [
                      {
                        controls: [
                          {
                            type: "combobox",
                            name: "company",
                            label: "Company",
                            request: companiesRequest,
                          },

                          {
                            type: "areabox",
                            name: "description",
                            label: "Description",
                          },
                          {
                            type: "togglebox",
                            name: "isPaid",
                            label: "Paid",
                            checkedText: "Yes",
                            unCheckedText: "No",
                          },
                          {
                            type: "datepicker",
                            name: "dueDate",
                            label: "Due Date",
                            rules: [
                              {
                                required: true,
                                message: "Due Date is required",
                              },
                            ],
                          },
                          {
                            type: "textbox",
                            name: "totalAmount",
                            label: "TotalAmount",
                          },
                        ],
                      },
                    ],
                  }}
                />
              </Col>
              {props.mode === "update" && props.uid && (
                <Col className="flex-1">
                  <InvoiceItemsManagment
                    invoiceItems={invoiceItems}
                    invoiceUid={props.uid}
                    loadItems={loadInvoiceItems}
                    setError={setErrorMessage}
                  />
                </Col>
              )}
            </Row>
          )}
          {props.mode === "delete" && (
            <Typography.Paragraph>
              <Typography.Text>Continue deleting the invoice?</Typography.Text>
            </Typography.Paragraph>
          )}

          {errorMessage && (
            <Alert className="mb-2" message={errorMessage} type="error" />
          )}
        </InModal>
      )}
    </>
  );
}

export default InvoiceView;
